import {displayShortDate} from "@ui-kit/framework/util/static/date-format-utils";
import {displayString} from "@ui-kit/framework/util/static/string-format-util";
import {
  Date,
  DefaultSearchTerms,
  DisplayFunction,
  DisplayOrder,
  ExportFunction,
  Number,
  String,
  TableDisplayedDefault,
  TableIdentifier
} from "@ui-kit/webkit/components/crud-item/crud-item-decorator";
import {OfferingSpacesInterface, SpaceType} from "./offering-spaces.interface";
import {SpaceDefaultRate} from "./space-default-pricing.interface";
import {Industry, IndustryCategory} from "../../settings/industries/industry.model";

interface CrudSpacesModel {
  id: number;
  spaceName: string;
  spaceSize: number;
  startDate: Date;
  endDate: Date | null;
  industryLabels: string;
  status: string;
}

@DefaultSearchTerms(['status:Active'])
export class CrudSpaces implements CrudSpacesModel {

  @DisplayOrder(1)
  @TableDisplayedDefault
  @TableIdentifier
  @Number("ID")
  id!: number;

  @DisplayOrder(2)
  @TableDisplayedDefault
  @String("Name")
  spaceName!: string;

  @DisplayOrder(3)
  @TableDisplayedDefault
  @Number("Size")
  spaceSize!: number;

  @DisplayOrder(4)
  @DisplayFunction(displayShortDate)
  @ExportFunction(displayShortDate)
  @TableDisplayedDefault
  @Date("Available from", "YYYY-MM-DD")
  startDate!: Date;

  @DisplayOrder(5)
  @DisplayFunction(displayShortDate)
  @ExportFunction(displayShortDate)
  @TableDisplayedDefault
  @Date("Available until", "YYYY-MM-DD")
  endDate!: Date | null;

  @DisplayOrder(6)
  @DisplayFunction(displayString)
  @TableDisplayedDefault
  @String("Industry")
  industryLabels!: string;

  @DisplayOrder(7)
  @TableDisplayedDefault
  @String("Status")
  status!: string;

  // Additional properties from OfferingSpacesInterface that we need but don't display
  districtUuid!: string;
  spaceType!: SpaceType;
  locked!: boolean;
  hasHadLease!: boolean;
  earliestEndDate!: Date | null;
  latestStartDate!: Date | null;
  districtSpacesIndustries!: string[];
  defaultPricing?: SpaceDefaultRate[];

  constructor(obj: OfferingSpacesInterface | any = {}, allIndustries: IndustryCategory[]) {
    obj = obj || {};
    this.id = obj.id;
    this.spaceName = obj.spaceName;
    this.spaceSize = obj.spaceSize;
    this.startDate = obj.startDate;
    this.endDate = obj.endDate;
    this.districtUuid = obj.districtUuid;
    this.spaceType = obj.spaceType;
    this.locked = obj.locked;
    this.hasHadLease = obj.hasHadLease;
    this.earliestEndDate = obj.earliestEndDate;
    this.latestStartDate = obj.latestStartDate;
    this.districtSpacesIndustries = obj.districtSpacesIndustries || [];
    this.defaultPricing = obj.defaultPricing || [];

    // Computed properties
    this.industryLabels = this.getIndustryLabels(this.districtSpacesIndustries, allIndustries);
    this.status = this.locked ? 'Expired' : 'Active';
  }

  private getIndustryLabels(industryCodes: string[] | null, allIndustries: IndustryCategory[]): string {
    if (industryCodes && allIndustries) {
      return allIndustries
        .filter(f => industryCodes.includes(f.code))
        .map(f => f.name)
        .join(', ');
    } else {
      return '';
    }
  }

  // Convert back to OfferingSpacesInterface for compatibility with existing code
  toOfferingSpacesInterface(): OfferingSpacesInterface {
    return {
      id: this.id,
      districtUuid: this.districtUuid,
      spaceType: this.spaceType,
      spaceName: this.spaceName,
      spaceSize: this.spaceSize,
      startDate: this.startDate,
      endDate: this.endDate,
      locked: this.locked,
      hasHadLease: this.hasHadLease,
      earliestEndDate: this.earliestEndDate,
      latestStartDate: this.latestStartDate,
      districtSpacesIndustries: this.districtSpacesIndustries,
      defaultPricing: this.defaultPricing || []
    };
  }
}
